import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, BarChart2, Shield, TrendingUp, Database, Activity,
  UserPlus, Trash2, RefreshCw, ChevronDown, Search, X,
  Crown, AlertTriangle, CheckCircle, Clock, ArrowLeft, Settings
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import './AdminDashboard.css';

const TOKEN_KEY = 'rmw_token';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`/api/admin${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
};

// ── Simple SVG charts ──────────────────────────────────────────────────────────

const TIER_COLORS = {
  free: '#94a3b8', basic: '#0ea5e9', pro: '#4f46e5',
  advanced: '#7c3aed', super: '#f59e0b',
};
const TIER_LABELS = {
  free: 'Free', basic: 'Basic', pro: 'Pro', advanced: 'Advanced', super: 'Super',
};

function DonutChart({ data, size = 160 }) {
  const total = data.reduce((s, d) => s + parseInt(d.count), 0) || 1;
  let offset = 0;
  const r = 55, cx = size / 2, cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const segments = data.map((d) => {
    const pct = parseInt(d.count) / total;
    const dash = pct * circumference;
    const seg = { ...d, dash, offset, pct };
    offset += dash;
    return seg;
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e5e7eb" strokeWidth="22" />
      {segments.map((seg, i) => (
        <circle
          key={i} cx={cx} cy={cy} r={r} fill="none"
          stroke={TIER_COLORS[seg.subscription_tier] || '#94a3b8'}
          strokeWidth="22"
          strokeDasharray={`${seg.dash} ${circumference - seg.dash}`}
          strokeDashoffset={-seg.offset}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      ))}
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize="22" fontWeight="700" fill="#1f2937">{total}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize="11" fill="#6b7280">users</text>
    </svg>
  );
}

function GrowthChart({ data }) {
  if (!data || data.length === 0) return <div className="chart-empty">No data yet</div>;
  const max = Math.max(...data.map(d => parseInt(d.count)), 1);
  const w = 100 / data.length;
  return (
    <div className="growth-chart">
      {data.map((d, i) => {
        const h = (parseInt(d.count) / max) * 100;
        return (
          <div key={i} className="growth-bar-wrap" style={{ width: `${w}%` }} title={`${d.day}: ${d.count} signups`}>
            <div className="growth-bar" style={{ height: `${h}%` }} />
          </div>
        );
      })}
    </div>
  );
}

function WizardUsageChart({ data }) {
  if (!data) return null;
  const wizards = [
    { key: 'gap_finder_used', label: 'A: Gap Finder', color: '#0d9488' },
    { key: 'wizard_a_complete', label: 'B: Methodology', color: '#4f46e5' },
    { key: 'wizard_b_complete', label: 'C: Data Analysis', color: '#0891b2' },
    { key: 'framework_complete', label: 'D: Framework', color: '#7c3aed' },
    { key: 'data_collection_complete', label: 'E: Data Collection', color: '#059669' },
    { key: 'wizard_e_complete', label: 'F: Ethics', color: '#dc2626' },
  ];
  const total = Math.max(parseInt(data.total_designs) || 1, 1);
  return (
    <div className="usage-chart">
      {wizards.map((w) => {
        const val = parseInt(data[w.key]) || 0;
        const pct = Math.round((val / total) * 100);
        return (
          <div key={w.key} className="usage-row">
            <span className="usage-label">{w.label}</span>
            <div className="usage-bar-bg">
              <div className="usage-bar-fill" style={{ width: `${pct}%`, background: w.color }} />
            </div>
            <span className="usage-count">{val} <span className="usage-pct">({pct}%)</span></span>
          </div>
        );
      })}
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="stat-card" style={{ '--stat-color': color }}>
      <div className="stat-icon"><Icon size={22} /></div>
      <div className="stat-body">
        <p className="stat-label">{label}</p>
        <p className="stat-value">{value ?? '—'}</p>
        {sub && <p className="stat-sub">{sub}</p>}
      </div>
    </div>
  );
}

// ── Revenue estimate ──────────────────────────────────────────────────────────

const TIER_PRICES = { free: 0, basic: 2, pro: 4, advanced: 6, super: 5 };

function estimateRevenue(tiers) {
  return tiers.reduce((s, t) => s + (TIER_PRICES[t.subscription_tier] || 0) * parseInt(t.count), 0).toFixed(2);
}

// ── User Row ──────────────────────────────────────────────────────────────────

const ROLE_LABELS = { user: 'User', sub_admin: 'Sub-Admin', super_admin: 'Super Admin' };

function UserRow({ user, isSuperAdmin, currentUserId, onTierChange, onRoleChange, onDelete }) {
  const [showTier, setShowTier] = useState(false);
  const [showRole, setShowRole] = useState(false);
  const isAdmin = user.role === 'sub_admin' || user.role === 'super_admin';
  const canDelete = !isAdmin || isSuperAdmin;
  const canChangeRole = isSuperAdmin;

  return (
    <tr className={`user-row ${isAdmin ? 'admin-row' : ''}`}>
      <td>
        <div className="user-cell">
          <span className="user-avatar-sm">{(user.display_name || user.username)[0].toUpperCase()}</span>
          <div>
            <div className="user-name-cell">{user.display_name || user.username}</div>
            <div className="user-username-cell">@{user.username}</div>
          </div>
        </div>
      </td>
      <td>
        <span className={`role-badge role-${user.role}`}>{ROLE_LABELS[user.role] || user.role}</span>
      </td>
      <td>
        <div className="tier-select-wrap">
          <button className={`tier-btn tier-${user.subscription_tier}`} onClick={() => setShowTier(!showTier)}>
            {TIER_LABELS[user.subscription_tier] || user.subscription_tier} <ChevronDown size={12} />
          </button>
          {showTier && (
            <div className="tier-dropdown">
              {['free', 'basic', 'pro', 'advanced', 'super'].map(t => (
                <button key={t} onClick={() => { onTierChange(user.id, t); setShowTier(false); }}
                  className={user.subscription_tier === t ? 'active' : ''}>
                  {TIER_LABELS[t]}
                </button>
              ))}
            </div>
          )}
        </div>
      </td>
      <td className="ta-center">{user.design_count || 0}</td>
      <td>{new Date(user.created_at).toLocaleDateString()}</td>
      <td>{user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : '—'}</td>
      <td>
        <div className="row-actions">
          {canChangeRole && user.id !== currentUserId && (
            <div className="role-select-wrap">
              <button className="admin-action-btn role-btn" onClick={() => setShowRole(!showRole)} title="Change role">
                <Shield size={14} />
              </button>
              {showRole && (
                <div className="tier-dropdown">
                  {['user', 'sub_admin', 'super_admin'].map(r => (
                    <button key={r} onClick={() => { onRoleChange(user.id, r); setShowRole(false); }}
                      className={user.role === r ? 'active' : ''}>
                      {ROLE_LABELS[r]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {canDelete && user.id !== currentUserId && (
            <button className="admin-action-btn admin-delete-btn" onClick={() => onDelete(user)} title="Delete user">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Main AdminDashboard ────────────────────────────────────────────────────────

export default function AdminDashboard({ onBack }) {
  const { currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [tab, setTab] = useState('overview');
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const [newAdmin, setNewAdmin] = useState({ username: '', password: '', displayName: '', role: 'sub_admin' });
  const [creating, setCreating] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [setupForm, setSetupForm] = useState({ username: '', password: '', displayName: '', setupKey: '' });
  const [refreshKey, setRefreshKey] = useState(0);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/stats');
      setStats(data);
      setLogs(data.recentLogs || []);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set('search', search);
      if (filterTier) params.set('tier', filterTier);
      const data = await apiFetch(`/users?${params}`);
      setUsers(data.users || []);
      setTotalUsers(data.total || 0);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setUsersLoading(false);
    }
  }, [page, search, filterTier]);

  useEffect(() => { loadStats(); }, [loadStats, refreshKey]);
  useEffect(() => { if (tab === 'users' || tab === 'admins') loadUsers(); }, [tab, loadUsers, refreshKey]);

  const handleTierChange = async (userId, tier) => {
    try {
      await apiFetch(`/users/${userId}/tier`, { method: 'PUT', body: JSON.stringify({ tier }) });
      showToast('Subscription tier updated');
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, subscription_tier: tier } : u));
    } catch (e) { showToast(e.message, 'error'); }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await apiFetch(`/users/${userId}/role`, { method: 'PUT', body: JSON.stringify({ role }) });
      showToast('Role updated');
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
      setRefreshKey(k => k + 1);
    } catch (e) { showToast(e.message, 'error'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiFetch(`/users/${deleteTarget.id}`, { method: 'DELETE' });
      showToast(`User @${deleteTarget.username} deleted`);
      setDeleteTarget(null);
      setRefreshKey(k => k + 1);
    } catch (e) { showToast(e.message, 'error'); setDeleteTarget(null); }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await apiFetch('/users', { method: 'POST', body: JSON.stringify(newAdmin) });
      showToast('Admin account created');
      setNewAdmin({ username: '', password: '', displayName: '', role: 'sub_admin' });
      setRefreshKey(k => k + 1);
    } catch (e) { showToast(e.message, 'error'); }
    setCreating(false);
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    try {
      await apiFetch('/setup', { method: 'POST', body: JSON.stringify(setupForm) });
      showToast('Super admin created! Please sign in again.');
      setShowSetup(false);
    } catch (e) { showToast(e.message, 'error'); }
  };

  const revenue = stats ? estimateRevenue(stats.tierBreakdown || []) : '0.00';

  const admins = users.filter(u => u.role === 'sub_admin' || u.role === 'super_admin');
  const regularUsers = users.filter(u => u.role === 'user');

  return (
    <div className="admin-dashboard">
      {toast && (
        <div className={`admin-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          {toast.msg}
        </div>
      )}

      {deleteTarget && (
        <div className="confirm-overlay">
          <div className="confirm-modal">
            <AlertTriangle size={32} color="#dc2626" />
            <h3>Delete User</h3>
            <p>Delete <strong>@{deleteTarget.username}</strong> and all their designs? This cannot be undone.</p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn-confirm-delete" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-header">
        <button className="admin-back-btn" onClick={onBack}>
          <ArrowLeft size={18} /> Back to App
        </button>
        <div className="admin-title-wrap">
          <Shield size={28} color="#4f46e5" />
          <div>
            <h1 className="admin-title">Admin Dashboard</h1>
            <p className="admin-subtitle">
              {isSuperAdmin ? 'Super Admin' : 'Sub-Admin'} · {currentUser?.displayName || currentUser?.username}
            </p>
          </div>
        </div>
        <button className="admin-refresh-btn" onClick={() => setRefreshKey(k => k + 1)} title="Refresh">
          <RefreshCw size={16} />
        </button>
      </div>

      <nav className="admin-tabs">
        {['overview', 'users', 'admins', 'activity'].map(t => (
          <button key={t} className={`admin-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t === 'overview' && <BarChart2 size={16} />}
            {t === 'users' && <Users size={16} />}
            {t === 'admins' && <Shield size={16} />}
            {t === 'activity' && <Activity size={16} />}
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </nav>

      <div className="admin-body">

        {/* ── OVERVIEW ── */}
        {tab === 'overview' && (
          loading ? <div className="admin-loading"><RefreshCw size={24} className="spin" /> Loading stats…</div> : stats ? (
            <>
              <div className="stat-grid">
                <StatCard icon={Users} label="Total Users" color="#4f46e5"
                  value={parseInt(stats.userStats?.total || 0) + parseInt(stats.roleBreakdown?.find(r => r.role === 'sub_admin')?.count || 0) + parseInt(stats.roleBreakdown?.find(r => r.role === 'super_admin')?.count || 0)}
                  sub={`+${stats.userStats?.new_this_week || 0} this week`} />
                <StatCard icon={TrendingUp} label="New This Month" color="#059669"
                  value={stats.userStats?.new_this_month || 0}
                  sub={`${stats.userStats?.active_today || 0} active today`} />
                <StatCard icon={Database} label="Total Designs" color="#0891b2"
                  value={stats.designStats?.total_designs || 0}
                  sub={`Avg ${stats.designStats?.avg_per_user || 0} per user`} />
                <StatCard icon={Crown} label="Est. Monthly Revenue" color="#f59e0b"
                  value={`$${revenue}`}
                  sub="Based on active subscriptions" />
                <StatCard icon={Activity} label="Active This Week" color="#7c3aed"
                  value={stats.userStats?.active_this_week || 0}
                  sub="Logged in last 7 days" />
                <StatCard icon={CheckCircle} label="Paid Subscribers" color="#dc2626"
                  value={(stats.tierBreakdown || []).filter(t => t.subscription_tier !== 'free').reduce((s, t) => s + parseInt(t.count), 0)}
                  sub="Basic + Pro + Advanced + Super" />
              </div>

              <div className="charts-grid">
                <div className="chart-card">
                  <h3 className="chart-title">Subscription Breakdown</h3>
                  <div className="donut-wrap">
                    <DonutChart data={stats.tierBreakdown || []} />
                    <div className="donut-legend">
                      {(stats.tierBreakdown || []).map(t => (
                        <div key={t.subscription_tier} className="legend-item">
                          <span className="legend-dot" style={{ background: TIER_COLORS[t.subscription_tier] || '#94a3b8' }} />
                          <span>{TIER_LABELS[t.subscription_tier] || t.subscription_tier}</span>
                          <span className="legend-count">{t.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="chart-card">
                  <h3 className="chart-title">User Growth — Last 30 Days</h3>
                  <GrowthChart data={stats.userGrowth || []} />
                </div>

                <div className="chart-card">
                  <h3 className="chart-title">Wizard Usage (by Completions)</h3>
                  <WizardUsageChart data={stats.wizardCompletion} />
                </div>
              </div>

              <div className="overview-bottom">
                <div className="overview-table-card">
                  <h3 className="chart-title">Recent Signups</h3>
                  <table className="mini-table">
                    <thead><tr><th>User</th><th>Tier</th><th>Designs</th><th>Joined</th></tr></thead>
                    <tbody>
                      {(stats.recentUsers || []).map(u => (
                        <tr key={u.id}>
                          <td>
                            <div className="user-cell-sm">
                              <span className="user-avatar-sm">{(u.display_name || u.username)[0].toUpperCase()}</span>
                              @{u.username}
                            </div>
                          </td>
                          <td><span className={`tier-pill tier-${u.subscription_tier}`}>{TIER_LABELS[u.subscription_tier] || u.subscription_tier}</span></td>
                          <td className="ta-center">{u.design_count || 0}</td>
                          <td>{new Date(u.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="overview-table-card">
                  <h3 className="chart-title">Top Users by Designs</h3>
                  <table className="mini-table">
                    <thead><tr><th>User</th><th>Tier</th><th>Designs</th></tr></thead>
                    <tbody>
                      {(stats.topUsers || []).map((u, i) => (
                        <tr key={i}>
                          <td>
                            <div className="user-cell-sm">
                              <span className="user-avatar-sm">{(u.display_name || u.username)[0].toUpperCase()}</span>
                              @{u.username}
                            </div>
                          </td>
                          <td><span className={`tier-pill tier-${u.subscription_tier}`}>{TIER_LABELS[u.subscription_tier] || u.subscription_tier}</span></td>
                          <td className="ta-center">{u.design_count || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : <div className="admin-empty">Failed to load stats.</div>
        )}

        {/* ── USERS ── */}
        {tab === 'users' && (
          <>
            <div className="users-toolbar">
              <div className="search-wrap">
                <Search size={16} />
                <input placeholder="Search by username or name…" value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }} />
                {search && <button onClick={() => setSearch('')}><X size={14} /></button>}
              </div>
              <select value={filterTier} onChange={e => { setFilterTier(e.target.value); setPage(1); }}>
                <option value="">All Tiers</option>
                {['free','basic','pro','advanced','super'].map(t => <option key={t} value={t}>{TIER_LABELS[t]}</option>)}
              </select>
              <span className="user-count">{totalUsers} users</span>
            </div>

            <div className="users-table-wrap">
              {usersLoading ? (
                <div className="admin-loading"><RefreshCw size={20} className="spin" /> Loading…</div>
              ) : (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>User</th><th>Role</th><th>Tier</th>
                      <th className="ta-center">Designs</th><th>Joined</th><th>Last Login</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regularUsers.length === 0 ? (
                      <tr><td colSpan="7" className="empty-row">No users found.</td></tr>
                    ) : regularUsers.map(u => (
                      <UserRow key={u.id} user={u} isSuperAdmin={isSuperAdmin}
                        currentUserId={currentUser?.id}
                        onTierChange={handleTierChange}
                        onRoleChange={handleRoleChange}
                        onDelete={setDeleteTarget} />
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {totalUsers > 20 && (
              <div className="pagination">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                <span>Page {page} of {Math.ceil(totalUsers / 20)}</span>
                <button disabled={page >= Math.ceil(totalUsers / 20)} onClick={() => setPage(p => p + 1)}>Next</button>
              </div>
            )}
          </>
        )}

        {/* ── ADMINS ── */}
        {tab === 'admins' && (
          <>
            {isSuperAdmin && (
              <div className="create-admin-card">
                <h3><UserPlus size={18} /> Create Admin Account</h3>
                <form onSubmit={handleCreateAdmin} className="create-admin-form">
                  <input placeholder="Username" required value={newAdmin.username}
                    onChange={e => setNewAdmin(p => ({ ...p, username: e.target.value }))} />
                  <input placeholder="Display Name" value={newAdmin.displayName}
                    onChange={e => setNewAdmin(p => ({ ...p, displayName: e.target.value }))} />
                  <input type="password" placeholder="Password" required value={newAdmin.password}
                    onChange={e => setNewAdmin(p => ({ ...p, password: e.target.value }))} />
                  <select value={newAdmin.role} onChange={e => setNewAdmin(p => ({ ...p, role: e.target.value }))}>
                    <option value="sub_admin">Sub-Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                  <button type="submit" className="btn-create-admin" disabled={creating}>
                    {creating ? 'Creating…' : 'Create Account'}
                  </button>
                </form>
              </div>
            )}

            <div className="users-table-wrap">
              {usersLoading ? (
                <div className="admin-loading"><RefreshCw size={20} className="spin" /> Loading…</div>
              ) : (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Admin</th><th>Role</th><th>Tier</th>
                      <th className="ta-center">Designs</th><th>Joined</th><th>Last Login</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.length === 0 ? (
                      <tr><td colSpan="7" className="empty-row">No admin accounts found.</td></tr>
                    ) : admins.map(u => (
                      <UserRow key={u.id} user={u} isSuperAdmin={isSuperAdmin}
                        currentUserId={currentUser?.id}
                        onTierChange={handleTierChange}
                        onRoleChange={handleRoleChange}
                        onDelete={isSuperAdmin ? setDeleteTarget : null} />
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {isSuperAdmin && (
              <div className="setup-section">
                <button className="btn-setup-toggle" onClick={() => setShowSetup(!showSetup)}>
                  <Settings size={16} /> First-Time Super Admin Setup
                </button>
                {showSetup && (
                  <form onSubmit={handleSetup} className="setup-form">
                    <p className="setup-hint">Use this form once to create the very first super admin account, or to promote an existing user. Requires the setup key.</p>
                    <input placeholder="Username" required value={setupForm.username}
                      onChange={e => setSetupForm(p => ({ ...p, username: e.target.value }))} />
                    <input placeholder="Display Name" value={setupForm.displayName}
                      onChange={e => setSetupForm(p => ({ ...p, displayName: e.target.value }))} />
                    <input type="password" placeholder="Password" required value={setupForm.password}
                      onChange={e => setSetupForm(p => ({ ...p, password: e.target.value }))} />
                    <input placeholder="Setup Key" required value={setupForm.setupKey}
                      onChange={e => setSetupForm(p => ({ ...p, setupKey: e.target.value }))} />
                    <button type="submit" className="btn-create-admin">Create Super Admin</button>
                  </form>
                )}
              </div>
            )}
          </>
        )}

        {/* ── ACTIVITY LOG ── */}
        {tab === 'activity' && (
          <div className="activity-log">
            <h3 className="chart-title">Recent Admin Activity</h3>
            {logs.length === 0 ? (
              <div className="admin-empty">No admin activity recorded yet.</div>
            ) : (
              <div className="log-list">
                {logs.map((log, i) => (
                  <div key={i} className="log-item">
                    <div className="log-icon">
                      {log.action === 'delete_user' ? <Trash2 size={14} color="#dc2626" /> :
                       log.action === 'change_role' ? <Shield size={14} color="#4f46e5" /> :
                       log.action === 'change_tier' ? <Crown size={14} color="#f59e0b" /> :
                       log.action === 'create_admin' ? <UserPlus size={14} color="#059669" /> :
                       <Activity size={14} color="#6b7280" />}
                    </div>
                    <div className="log-body">
                      <p className="log-text">
                        <strong>@{log.admin_username}</strong>{' '}
                        {log.action.replace(/_/g, ' ')}
                        {log.target_username && <> → <strong>@{log.target_username}</strong></>}
                        {log.details && Object.keys(log.details).length > 0 && (
                          <span className="log-details"> ({Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ')})</span>
                        )}
                      </p>
                      <p className="log-time">
                        <Clock size={11} /> {new Date(log.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
